import { Button, FilterSelect } from '@/components/ui';
import { CHANNELS, DELIVERY_CHANNEL_IDS } from '@/config/channels';
import { cn } from '@/utils/cn';

// 06.04.26: фильтр вынесен в отдельный reusable-компонент, чтобы его можно было
// использовать не только на /patients. Он работает поверх общего channel registry,
// а не хранит собственный список названий/порядка каналов.
export interface PatientChannelFilterValue {
  matchMode: 'any' | 'all';
  channels: string[];
  excludeChannels: string[];
  preferredChannel: string;
  contactMode: string;
}

interface PatientChannelFiltersProps {
  value: PatientChannelFilterValue;
  onChange: (next: PatientChannelFilterValue) => void;
  onReset: () => void;
}

const matchModeOptions = [
  { value: 'any', label: 'Любой из выбранных' },
  { value: 'all', label: 'Все выбранные сразу' },
];

const preferredChannelOptions = [
  { value: 'none', label: 'Только Auto (без preferred_channel)' },
  ...DELIVERY_CHANNEL_IDS.map((channelId) => ({
    value: channelId,
    label: CHANNELS[channelId].displayName || CHANNELS[channelId].name,
  })),
];

function toggleValue(items: string[], value: string): string[] {
  if (items.includes(value)) {
    return items.filter((item) => item !== value);
  }
  return [...items, value];
}

function ChannelChecklist({
  title,
  description,
  selected,
  onToggle,
}: {
  title: string;
  description: string;
  selected: string[];
  onToggle: (value: string) => void;
}) {
  return (
    <div className="space-y-2">
      <div>
        <div className="text-sm font-medium text-neutral-900">{title}</div>
        <div className="text-xs text-neutral-500">{description}</div>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {DELIVERY_CHANNEL_IDS.map((channelId) => {
          const config = CHANNELS[channelId];
          const checked = selected.includes(channelId);
          return (
            <label
              key={channelId}
              className={cn(
                'flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 transition-colors',
                checked
                  ? 'border-primary-400 bg-primary-50 text-primary-900'
                  : 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300'
              )}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(channelId)}
                className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium">
                {config.icon} {config.displayName || config.name}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

const contactModeOptions = [
  { value: 'any', label: '∞ Любой контакт' },
  { value: 'written_only', label: '✎ Только письменно' },
  { value: 'voice_only', label: '☎ Только звонок' },
  { value: 'do_not_contact', label: '⊘ Не беспокоить' },
];

export function PatientChannelFilters({ value, onChange, onReset }: PatientChannelFiltersProps) {
  const hasActiveFilters =
    value.channels.length > 0 ||
    value.excludeChannels.length > 0 ||
    value.preferredChannel !== '' ||
    value.contactMode !== '';

  return (
    <div className="space-y-4 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-neutral-900">Точные фильтры по каналам</div>
          <div className="text-xs text-neutral-500">
            Фильтруют по реальной активности/привязке из БД, а не по одному только наличию телефона.
          </div>
        </div>
        <Button variant="secondary" size="sm" onClick={onReset} disabled={!hasActiveFilters}>
          Сбросить каналы
        </Button>
      </div>

      <div className="grid gap-4 xl:grid-cols-[280px_320px_320px]">
        <div>
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-neutral-500">Логика совпадения</div>
          <FilterSelect
            value={value.matchMode}
            onChange={(next) => onChange({ ...value, matchMode: next === 'all' ? 'all' : 'any' })}
            options={matchModeOptions}
            showAllOption={false}
          />
        </div>
        <div>
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-neutral-500">Preferred channel</div>
          <FilterSelect
            value={value.preferredChannel}
            onChange={(preferredChannel) => onChange({ ...value, preferredChannel })}
            options={preferredChannelOptions}
            placeholder="Любой preferred channel"
            allOptionLabel="Любой preferred channel"
          />
        </div>
        <div>
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-neutral-500">Режим контакта</div>
          <FilterSelect
            value={value.contactMode}
            onChange={(contactMode) => onChange({ ...value, contactMode })}
            options={contactModeOptions}
            placeholder="Любой режим"
            allOptionLabel="Любой режим"
          />
        </div>
      </div>

      <ChannelChecklist
        title="У пациента есть канал"
        description="`any` — достаточно любого выбранного, `all` — должны совпасть все."
        selected={value.channels}
        onToggle={(channelId) => onChange({ ...value, channels: toggleValue(value.channels, channelId) })}
      />

      <ChannelChecklist
        title="У пациента не должно быть канала"
        description="Удобно искать, у кого, например, нет MAX или нет Telegram Client."
        selected={value.excludeChannels}
        onToggle={(channelId) => onChange({ ...value, excludeChannels: toggleValue(value.excludeChannels, channelId) })}
      />
    </div>
  );
}
